/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";
import { resolveReturnToHref, type LibrarySourceType } from "@/lib/library";
import type { ExcerptRow } from "@/components/excerpts/ExcerptCard";
import type { NamedItem } from "@/components/excerpts/ExcerptsBulkBar";

export type LibrarySource = {
  id: string; title: string; subtitle: string | null;
  display_title: string | null; author_text: string | null;
};

export function useExcerptsData() {
  const { active, workspaces, libraryMode } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();

  const tagFilterParam = searchParams.get("tag") ?? "";
  const topicFilterParam = searchParams.get("topic") ?? "";
  const tagFilters = useMemo(
    () => tagFilterParam.split(",").map((s) => s.trim()).filter((s) => s.length > 0),
    [tagFilterParam],
  );
  const topicFilters = useMemo(
    () => topicFilterParam.split(",").map((s) => s.trim()).filter((s) => s.length > 0),
    [topicFilterParam],
  );
  const tagFilterKey = tagFilters.join(",");
  const topicFilterKey = topicFilters.join(",");
  const libraryItemFilter = searchParams.get("libraryItem");
  const typeFilterParam = searchParams.get("type") ?? "";
  const sourceTypeFilters = useMemo<LibrarySourceType[]>(
    () =>
      typeFilterParam.split(",").map((s) => s.trim()).filter((s): s is NonNullable<LibrarySourceType> =>
        ["book", "journal_article", "chapter", "proceedings_article", "website", "report", "other"].includes(s),
      ),
    [typeFilterParam],
  );
  const searchQuery = searchParams.get("q") ?? "";
  const returnTo = searchParams.get("returnTo") ?? "";
  const typeFilterKey = sourceTypeFilters.join(",");

  const [items, setItems] = useState<ExcerptRow[]>([]);
  const [librarySource, setLibrarySource] = useState<LibrarySource | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [tags, setTags] = useState<NamedItem[]>([]);
  const [topics, setTopics] = useState<NamedItem[]>([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const scrollRestoredRef = useRef(false);


  const scopeWorkspaceIds = useMemo(() => {
    if (!active) return [] as string[];
    if (libraryMode === "shared" && workspaces.length > 1) return workspaces.map((w) => w.id);
    return [active.id];
  }, [active, libraryMode, workspaces]);

  const effectiveWorkspaceIds = useMemo(
    () => (selectedWorkspaceIds.length > 0 ? selectedWorkspaceIds : scopeWorkspaceIds),
    [selectedWorkspaceIds, scopeWorkspaceIds],
  );

  const scrollKey = useMemo(
    () => `excerptus.excerptsScroll:${searchQuery}|${tagFilterKey}|${topicFilterKey}|${libraryItemFilter ?? ""}|${typeFilterKey}`,
    [searchQuery, tagFilterKey, topicFilterKey, libraryItemFilter, typeFilterKey],
  );

  const excerptViewReturnTo = useMemo(() => {
    const next = new URLSearchParams();
    if (searchQuery) next.set("q", searchQuery);
    if (tagFilterKey) next.set("tag", tagFilterKey);
    if (topicFilterKey) next.set("topic", topicFilterKey);
    if (libraryItemFilter) next.set("libraryItem", libraryItemFilter);
    if (typeFilterKey) next.set("type", typeFilterKey);
    return `excerpts${next.toString() ? `?${next.toString()}` : ""}`;
  }, [topicFilterKey, libraryItemFilter, searchQuery, tagFilterKey, typeFilterKey]);

  const returnHref = useMemo(() => resolveReturnToHref(returnTo) || null, [returnTo]);

  const returnLabel = returnTo.startsWith("my-library") || returnTo.startsWith("my-library:")
    ? "Return to my source/excerpt view"
    : returnTo.startsWith("excerpts")
      ? "Return to my excerpt view"
      : returnTo.startsWith("readwise-highlights")
        ? "Return to Readwise highlights"
        : null;

  const PAGE_SIZE = 50;

  const runQuery = async (cursor: string | null, filterExcerptIds: string[] | null) => {
    let query = supabase
      .from("excerpts")
      .select(
        "id, body_html, body_text, source_url, notes:notes_preview, created_at, library_item_id, library_items(id, title, subtitle, display_title, author_text, published_year, source_type, container_title, accessed_date, library_item_categories(library_categories(id, name, color, workspace_id, created_at, updated_at))), excerpt_tags(tags(name)), excerpt_topics(content_framework_topics(label))"
      )
      .in("workspace_id", effectiveWorkspaceIds)
      .order("created_at", { ascending: false })
      .limit(PAGE_SIZE);

    if (cursor) query = query.lt("created_at", cursor);
    if (filterExcerptIds) query = query.in("id", filterExcerptIds);
    if (libraryItemFilter) query = query.eq("library_item_id", libraryItemFilter);

    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) {
      query = query.textSearch("search_tsv", trimmedQuery, { type: "websearch", config: "simple" });
    }

    const { data } = await query;
    let rows = ((data as any) ?? []) as ExcerptRow[];
    if (sourceTypeFilters.length > 0) {
      rows = rows.filter((row) => {
        const t = row.library_items?.source_type ?? null;
        return t ? sourceTypeFilters.includes(t) : false;
      });
    }
    return rows;
  };

  const buildFilterIds = async (): Promise<{ ids: string[] | null; empty: boolean }> => {
    const idSets: string[][] = [];

    if (tagFilters.length > 0) {
      const lowered = tagFilters.map((t) => t.toLowerCase());
      const { data: tagRows } = await supabase
        .from("tags").select("id, name").in("workspace_id", effectiveWorkspaceIds);
      const tagIds = (tagRows ?? [])
        .filter((r: any) => lowered.includes(String(r.name).toLowerCase()))
        .map((r: any) => r.id);
      if (tagIds.length === 0) return { ids: null, empty: true };
      const { data: links } = await supabase
        .from("excerpt_tags").select("excerpt_id").in("tag_id", tagIds);
      idSets.push(Array.from(new Set((links ?? []).map((l: any) => l.excerpt_id))));
    }

    if (topicFilters.length > 0) {
      const lowered = topicFilters.map((t) => t.toLowerCase());
      const { data: topicRows } = await supabase
        .from("content_framework_topics").select("id, label").in("workspace_id", effectiveWorkspaceIds);
      const topicIds = ((topicRows ?? []) as Array<{ id: string; label: string }>)
        .filter((r) => lowered.includes(String(r.label).toLowerCase()))
        .map((r) => r.id);
      if (topicIds.length === 0) return { ids: null, empty: true };
      const { data: links } = await supabase
        .from("excerpt_topics").select("excerpt_id").in("topic_id", topicIds);
      idSets.push(Array.from(new Set((links ?? []).map((l: any) => l.excerpt_id))));
    }

    if (idSets.length === 0) return { ids: null, empty: false };
    const intersection = idSets.reduce((acc, set) => acc.filter((entryId) => set.includes(entryId)));
    if (intersection.length === 0) return { ids: null, empty: true };
    return { ids: intersection, empty: false };
  };

  const load = async () => {
    if (!active) return;
    setLoading(true);
    const { ids: filterExcerptIds, empty } = await buildFilterIds();
    if (empty) {
      setItems([]);
      setHasMore(false);
      setLoading(false);
      return;
    }
    const rows = await runQuery(null, filterExcerptIds);
    setItems(rows);
    setHasMore(rows.length === PAGE_SIZE);
    setLoading(false);
  };

  const loadMore = async () => {
    if (!active || loadingMore || !hasMore || items.length === 0) return;
    setLoadingMore(true);
    const { ids: filterExcerptIds, empty } = await buildFilterIds();
    if (empty) {
      setLoadingMore(false);
      return;
    }
    const cursor = items[items.length - 1]?.created_at ?? null;
    const rows = await runQuery(cursor, filterExcerptIds);
    setItems((prev) => [...prev, ...rows]);
    setHasMore(rows.length === PAGE_SIZE);
    setLoadingMore(false);
  };


  useEffect(() => {
    load();
    if (effectiveWorkspaceIds.length === 0) return;
    supabase
      .from("tags").select("id, name").in("workspace_id", effectiveWorkspaceIds).order("name")
      .then(({ data }) => setTags((data as any) ?? []));
    if (active?.codex_enabled === false) {
      setTopics([]);
    } else {
      supabase
        .from("content_framework_topics").select("id, label").in("workspace_id", effectiveWorkspaceIds).order("label")
        .then(({ data }) => setTopics(((data as any[]) ?? []).map((r) => ({ id: r.id, name: r.label }))));
    }
    scrollRestoredRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tagFilterKey, topicFilterKey, searchQuery, libraryItemFilter, typeFilterKey, effectiveWorkspaceIds.join(",")]);

  useEffect(() => {
    if (!libraryItemFilter || effectiveWorkspaceIds.length === 0) {
      setLibrarySource(null);
      return;
    }
    supabase
      .from("library_items")
      .select("id, title, subtitle, display_title, author_text")
      .in("workspace_id", effectiveWorkspaceIds)
      .eq("id", libraryItemFilter)
      .maybeSingle()
      .then(({ data }) => setLibrarySource((data as LibrarySource | null) ?? null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryItemFilter, effectiveWorkspaceIds.join(",")]);

  useEffect(() => {
    if (loading || scrollRestoredRef.current) return;
    if (items.length === 0) return;
    const saved = sessionStorage.getItem(scrollKey);
    if (saved) {
      const y = Number(saved);
      if (!Number.isNaN(y)) {
        requestAnimationFrame(() => {
          const container = document.getElementById("app-scroll-container");
          if (container) container.scrollTop = y;
          else window.scrollTo(0, y);
        });
      }
      sessionStorage.removeItem(scrollKey);
    }
    scrollRestoredRef.current = true;
  }, [loading, items, scrollKey]);

  return {
    active, workspaces, libraryMode,
    searchParams, setSearchParams,
    tagFilters, topicFilters, sourceTypeFilters, searchQuery,
    libraryItemFilter, typeFilterKey, returnTo, returnHref, returnLabel,
    excerptViewReturnTo, scrollKey,
    items, setItems, loading, librarySource, tags, topics, setTags,
    selectedWorkspaceIds, setSelectedWorkspaceIds,
    hasMore, loadingMore, loadMore,
    load,
  };

}
